import { useContext } from "react";

import { CommentRead } from "@nens/rana/api-v1";
import { getRanaApiV1 } from "@nens/rana/api-v1";
import { TenantContext } from "@nens/ranalib";
import { useMutationWithErrorHandler } from "@nens/ranalib";
import { ApiError } from "@nens/react-query-utils";

import {
  useInvalidatePublicationCommentListQuery,
  usePublicationCommentListQuery,
  useUpdatePublicationCommentListQuery,
} from "@/queries/query-publication-comment";

import { PublicatonMapId } from "../../../types/publication";

type CreateCommentThreadProps = { location: [number, number]; comment: string };

const useCreator = () => {
  const authInfo = useContext(TenantContext);
  return authInfo.isPublic ? authInfo.user : undefined;
};

export const useCreateCommentThread = (
  publicationId: string,
  mapId: PublicatonMapId,
  onSuccess?: (comment: CommentRead) => void
) => {
  const creator = useCreator();
  const invalidatePublicationCommentListQuery = useInvalidatePublicationCommentListQuery();

  return useMutationWithErrorHandler<CommentRead, ApiError, CreateCommentThreadProps>(
    async ({ location, comment }) => {
      // no check needed for published version, backend will accept comments in drafts for collaborators
      if (!mapId) {
        throw new Error("Cannot create comment: map is not (yet) loaded");
      }

      const response = await getRanaApiV1().PublicationCommentsApi.commentCreate({
        publicationId,
        commentCreate: {
          messages: [{ body: comment, creator }],
          location: { type: "Point", coordinates: location },
          creator,
          mapId,
        },
      });

      await invalidatePublicationCommentListQuery(publicationId, mapId);

      return response;
    },
    {
      onSuccess,
    }
  );
};

type AddCommentProps = { threadId: string; comment: string };

export const useAddComment = (publicationId: string, mapId: PublicatonMapId) => {
  const { user } = useContext(TenantContext);
  const creator = useCreator();
  const updatePublicationCommentListQuery = useUpdatePublicationCommentListQuery(publicationId);
  const { data } = usePublicationCommentListQuery(publicationId, mapId);

  return useMutationWithErrorHandler<void, ApiError, AddCommentProps>(
    async ({ threadId, comment }) => {
      const currentMessage = data?.items.find(({ id }) => id === threadId);

      // Immediatly add temporary message to the thread
      if (currentMessage) {
        updatePublicationCommentListQuery(currentMessage.id, {
          ...currentMessage,
          messages: [
            ...(currentMessage.messages ?? []),
            {
              body: comment,
              // set an initial value for id for User, for Creator it is not needed
              creator: { ...user, id: "id" in user ? user.id : "-" },
              createdAt: new Date(),
              updatedAt: new Date(),
              apiKeyId: "",
              deleted: false,
              edited: false,
              type: undefined as unknown as "unresolved", // For some reason this is typed like its required. Its not and should be undefined for new messages
            },
          ],
        });
      }

      const message = await getRanaApiV1().PublicationCommentsApi.commentAddMessage({
        publicationId,
        commentId: threadId,
        messageCreate: { body: comment, creator },
      });

      // Update to 100% correct message
      updatePublicationCommentListQuery(message.id, message);
    }
  );
};
